/* global navigator */

define([
  'lib/sumome/service/index',
  'underscore',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/listbuilder/popup',
  'es6!launcher/util/listbuilder/constants',
], (SumoService, _, ListBuilderPopup, Constants) => {

  class ListBuilderService extends SumoService.Service {
    static SHOWN_COOKIE = '__smListBuilderShown';
    static OPT_OUT_COOKIE = '__smListBuilderOptOut';

    constructor(api, service, popup, opts = {}) {
      super(api);

      this.isSocialProof = popup?.goal === Constants.FormGoals.SOCIAL_PROOF;

      if (popup && opts.isPreview) {
        this.popup = new ListBuilderPopup(this, {
          ...popup,
          isPreview: true,
        });

        return this.onShow(null, this.popup);
      }

      this.fetchFeatures().then(() => {
        this.service = service;

        this.showTimeoutId = false;
        this.hasMouse = false;
        this.scrollTop = -1;
        this.lmx = -1;
        this.lmy = -1;
        this.popup = null;
        this.triggerPercent = popup?.trigger_percent;
        this.hasAlreadyShown = false;
        this.isShownOnlyOnce = false;

        this.onShow = this.onShow.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onScroll = this.onScroll.bind(this);

        // Set up cookie functions (list builder needs the Getters for Click Triggers)
        this.setOptOutCookie = service.setOptOutCookie.bind(this, ListBuilderService.OPT_OUT_COOKIE);
        this.getOptOutCookie = service.getOptOutCookie.bind(this, ListBuilderService.OPT_OUT_COOKIE);
        this.setShownCookie = service.setShownCookie.bind(this, ListBuilderService.SHOWN_COOKIE);
        this.getShownCookie = service.getShownCookie.bind(this, ListBuilderService.SHOWN_COOKIE);
        this.setCtaCookie = service.setCtaCookie.bind(this);
        this.getCtaCookie = service.getCtaCookie.bind(this);
        this.setSubscribedCookie = service.setSubscribedCookie.bind(this);
        this.getSubscribedCookie = service.getSubscribedCookie.bind(this);
        this.setPushSubscribedCookie = service.setPushSubscribedCookie.bind(this);

        // If there isn't a popup, then this service is for click triggers.
        if (popup) {
          this.setupPopup(popup);
        }

        this.scrollElement = document.body;

        if (this.isSocialProof) {
          // Set as a single shown popup
          this.isShownOnlyOnce = true;

          // Get a scrollable element reference.
          if (Math.max(
            document.documentElement.scrollHeight,
            document.body.scrollHeight,
          ) <= window.innerHeight) {
            // Nowhere to scroll, find a possible scrollElement
            const elements = document.querySelectorAll('*');

            for (let i = 0; i < elements.length && this.scrollElement === document.body; i++) {
              if (elements[i].scrollHeight > window.innerHeight) {
                this.scrollElement = elements[i];
              }
            }
          }

          // Set onScroll behavior for valid trigger points (if applies).
          if (this.triggerPercent > 0) {
            this.addEvent(window, 'scroll', this.onScroll);
          }

          // Set timeout call based on "before show" delay value (if applies).
          const popupMode = this.popup.settings.get('popup_mode');
          const beforeShowDelay = this.popup.settings.get('before_show_delay', 0);
          const beforeShowDelayInMs = beforeShowDelay * 1000;

          if (popupMode === 'manual' && beforeShowDelayInMs > 0) {
            setTimeout(() => {
              this.onShow(null, this.popup);
            }, beforeShowDelayInMs);
          }

          // If no limits restrictions are present, trigger popup immediately.
          if (this.triggerPercent === 0 && beforeShowDelayInMs === 0) {
            this.onShow(null, this.popup);
          }
        }
      });
    }

    onScroll(e) {
      if (this.triggerPercent === undefined || this.triggerPercent === null) {
        return;
      }

      if (this.triggerPercent === 0) {
        this.removeEvent(window, 'scroll', this.onScroll);
        this.onShow(null, this.popup);
        return;
      }

      let scrollTop = this.scrollElement.scrollTop || document.documentElement.scrollTop;
      const winHeight = window.innerHeight;
      const docHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const bottom = docHeight - winHeight;
      const threshold = Math.min(100, Math.max(0, (this.triggerPercent / 100)));
      const perc = Math.max(0, Math.min(1, scrollTop / bottom));

      if (perc >= threshold) {
        this.removeEvent(window, 'scroll', this.onScroll);
        this.onShow(null, this.popup);
      }
    }

    setupPopup(popup) {
      this.popup = new ListBuilderPopup(this, popup);

      const popupMode = this.popup.settings.get('popup_mode');

      if (this.popup.settings.get('form_type') === Constants.FormTypes.INLINE_POPUP) {
        this.onShow(null, this.popup);
      } else if (this.isSocialProof) {
        return;
      } else if (popupMode === 'smart') {
        this.showOnExit();
      } else if (popupMode === 'mixed') {
        this.showOnExit();
        this.showOnStartup(this.popup);
      } else {
        this.showOnStartup(this.popup);
      }
    }

    onShow(clickTrigger, popupToShow) {
      const popup = clickTrigger || popupToShow || this.popup;

      let existingPopup = false;

      // Check if this is a single show execution instance popup.
      if (this.hasAlreadyShown && this.isShownOnlyOnce) {
        return;
      }

      if (clickTrigger) {
        existingPopup = clickTrigger;
      }

      if (!existingPopup && popup.settings.get('form_type') !== Constants.FormTypes.INLINE_POPUP) this.removeAllHandlers();

      if (!existingPopup || (existingPopup && existingPopup.settings.get('form_type') === Constants.FormTypes.INLINE_POPUP)) {
        popup.show(!!clickTrigger);
      } else if (existingPopup) {
        existingPopup.show(!!clickTrigger);
      }

      // Set flag as the popup was already presented.
      this.hasAlreadyShown = true;
    }

    onClose(popupId) {
      document.documentElement.className = document.documentElement.className.replace(/(listbuilder\-popup\-open)/g, '');
    }

    onMouseMove(e) {
      const top = document.documentElement.scrollTop || document.body.scrollTop;
      const left = document.documentElement.scrollLeft || document.body.scrollLeft;

      this.hasMouse = true;

      // convert to local window position
      this.mx = e.pageX - left;
      this.my = e.pageY - top;

      if (this.lmx > -1) {
        this.travel = Math.max(Math.abs(this.mx - this.lmx), Math.abs(this.my - this.lmy)) || this.travel;
      }

      this.lmx = this.mx;
      this.lmy = this.my;
    }

    onMouseLeave(e) {
      const top = document.documentElement.scrollTop || document.body.scrollTop;
      const posy = e.pageY - top;

      if (posy <= 20 && this.travel >= 3) {
        this.onShow();
      }
    }

    onBlur(e) {
      const posy = this.my;
      const posx = this.mx;
      const width = document.documentElement.clientWidth || document.body.clientWidth;

      if (posy <= 20 && posx >= 20 && posx <= width - 20) {
        this.onShow();
      }
    }

    showOnStartup(popup) {
      let multiplier = 1000;

      if (popup.settings.get('before_show_delay_interval') === 'minute') multiplier = 1000 * 60;
      this.showTimeoutId = window.setTimeout(() => this.onShow(null, popup), popup.settings.get('before_show_delay') * multiplier);
    }

    showOnExit() {
      this.addEvent(document, 'mousemove', this.onMouseMove);
      this.addEvent(document, 'mouseleave', this.onMouseLeave);
      this.addEvent(window, 'blur', this.onBlur);

      if ('ontouchstart' in window || !!(navigator.msMaxTouchPoints)) {
        this.scrollTop = document.documentElement.scrollTop || document.body.scrollTop;

        this.addEvent(window, 'scroll', this.onScroll);
      } else {
        this.hasMouse = true;
      }
    }

    removeAllHandlers() {
      this.removeEvent(document, 'mousemove', this.onMouseMove);
      this.removeEvent(document, 'mouseleave', this.onMouseLeave);
      this.removeEvent(window, 'blur', this.onBlur);
      this.removeEvent(window, 'scroll', this.onScroll);

      if (this.showTimeoutId !== false) {
        window.clearTimeout(this.showTimeoutId);

        this.showTimeoutId = false;
      }
    }
  }

  return ListBuilderService;
});
